import { useState, useCallback, useEffect, useLayoutEffect, useRef, Suspense } from "react";
import { useSmoothAnchors } from "~/hooks/use-smooth-anchors";
import { useSessionCacheReset } from "~/hooks/use-session-cache-reset";
import { isOfflineFallbackPath, OFFLINE_DOCUMENT_NAMESPACES } from "~/lib/pwa/offline-fallback";
import { RouteTranslations } from "~/components/RouteTranslations";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
  useMatches,
  useRouteLoaderData,
  type LoaderFunctionArgs,
  type ShouldRevalidateFunctionArgs,
} from "react-router";
import * as Sentry from "@sentry/react";
import { rootShouldRevalidate } from "~/lib/network-failure-result";
import { boundaryReports } from "~/lib/error-ownership";
import { scrubPii } from "~/lib/pii-scrub";
import { isHydrating, markHydrated } from "~/lib/hydration-state";
import { useLeafRouteId } from "~/lib/boundary-route-id";
import {
  formatIncidentTimestamp,
  incidentIdFromHeaders,
  randomIncidentId,
  safeReferrer,
} from "~/lib/incident";
import type { Route } from "./+types/root";
import { useTranslation } from "react-i18next";
import { resolveRouteFromPathname } from "~/lib/locale-path";
import { ROUTE_CONFIG } from "~/config/route-config";
import i18nServer from "~/i18next.server";
import type { NavSessionUser } from "~/lib/nav-session";
import { safeGetSession } from "~/services/auth.server";
import { useCartStore } from "~/store/cartStore";
import {
  AppHeader,
  Cuter,
  Footer,
  CartSidebar,
  MemberHeader,
  TabBar,
  UiOverlay,
} from "~/components/layout";
import { CookieBanner } from "~/components/ui/CookieBanner";
import { AnalyticsProvider } from "~/components/analytics/AnalyticsProvider";
import { analyticsEnvironment, marketFromCountry } from "~/lib/analytics/events";
import { PwaProvider } from "~/components/pwa";
import { useUpdateSwitch } from "~/hooks/use-update-switch";
import { isPlayerPath } from "~/lib/pwa/update-switch";
import { ErrorPage404, ErrorPage500 } from "~/components/errors";
import { SkipToContent } from "~/components/layout/SkipToContent";
import { JsonLd } from "~/components/seo/JsonLd";
import { organizationJsonLd, websiteJsonLd } from "~/lib/seo";
import { MaintenanceBanner } from "~/components/MaintenanceBanner";
import { getMaintenanceState } from "~/lib/maintenance.server";
import { effectiveMode } from "~/lib/domain/maintenance";
import { cn } from "~/lib/cn";
import {
  STARTUP_IMAGE_STAMP,
  STARTUP_IMAGES,
  startupImageFile,
  startupImageMedia,
} from "~/lib/pwa/startup-images";
import "./styles/app.css";

export const links: Route.LinksFunction = () => [
  // T735 — the query string is a cache-buster, because artwork changes here land on the
  // SAME paths: bytes change, URLs do not, and every one of these is cached hard. A
  // browser keeps a favicon for days, iOS bakes the touch icon into the home-screen
  // shortcut the moment it is added, and Chrome decides whether to re-mint an INSTALLED
  // WebAPK by comparing the manifest — so an unchanged manifest leaves an already
  // installed member on the old icon indefinitely. That is why the manifest's icon `src`
  // values carry one too.
  //
  // Everything here is at `v=4`, one stamp for the four links and the eight manifest
  // entries. Until 2026-09-14 the tab and the launcher had moved on different days and
  // carried different numbers; the P8 kit replaced every mark at once, so no asset kept
  // its artwork and there is nothing left for a per-asset stamp to record.
  //
  // Two declarations, one drawing. The SVG is what modern browsers prefer and the only
  // one that stays crisp on a dense tab strip; the ICO is what Safari and older engines
  // read, and it carries native 16/32/48/64 frames rather than one image downscaled.
  // `tests/unit/infra/app-icons-are-the-brand-kit.test.ts` fails if a shipped file drifts
  // from the kit the brand record names, or if one of these stamps is left behind.
  { rel: "icon", href: "/favicon.svg?v=5", type: "image/svg+xml" },
  { rel: "icon", href: "/favicon.ico?v=5", sizes: "16x16 32x32 48x48 64x64" },
  { rel: "apple-touch-icon", href: "/apple-touch-icon.png?v=5" },
  // T978 — what iOS shows while the installed app opens; with no matching image it shows
  // black. One per portrait screen class, read from the same list the generator renders.
  ...STARTUP_IMAGES.map((image) => ({
    rel: "apple-touch-startup-image",
    media: startupImageMedia(image),
    href: `${startupImageFile(image)}?${STARTUP_IMAGE_STAMP}`,
  })),
  // NOTE: the manifest link is NOT here. `links` is static and cannot see the active
  // locale, and T52 ships one manifest per locale so the install carries her language's
  // name, start_url and shortcuts. It is emitted from the head render below instead.
  // T653 — the two Google preconnects and the css2 stylesheet are gone: the faces are
  // self-hosted (`@font-face` at the top of app.css, files under `public/fonts/`), so
  // there is no third-party origin to warm up and no render-blocking external
  // stylesheet before the first glyph.
];

/**
 * Re-run root loader on every navigation so `sessionUser` stays in sync with cookies (RR can
 * skip parent loaders on child-only transitions) — with the two narrow T828 exceptions around
 * `/evaluacion` documented on `rootShouldRevalidate` (a failed-fetch action; a same-language
 * GET into that route, which is already read on its own request).
 */
export function shouldRevalidate(args: ShouldRevalidateFunctionArgs) {
  return rootShouldRevalidate(args);
}

// The global layout (header, TabBar, footer) renders on every route via root.tsx,
// so its i18n namespaces must load for SSR on EVERY page — otherwise the chrome
// server-renders raw keys (e.g. "tabbar.dashboard") that flash until the client
// finishes loading them. getRouteNamespaces() (entry.server) collects this handle.
export const handle = { i18n: [...OFFLINE_DOCUMENT_NAMESPACES] };

export async function loader({ request, context }: LoaderFunctionArgs) {
  const locale = await i18nServer.getLocale(request);
  let sessionUser: NavSessionUser | null = null;
  // T817 — the member id PostHog identifies the browser under (the same one the server
  // events carry). Kept apart from `sessionUser`, which is display data for the chrome.
  let analyticsUserId: string | null = null;
  const env = (context as { cloudflare?: { env?: Env } } | undefined)?.cloudflare?.env;
  // T885 (audit F2) — the offline fallback is precached with the device's cookies and served
  // to whoever holds the device; its document must not embed anyone's identity.
  if (env?.DB && !isOfflineFallbackPath(new URL(request.url).pathname)) {
    const session = await safeGetSession(env, request);
    if (session?.user) {
      const u = session.user;
      analyticsUserId = typeof u.id === "string" ? u.id : null;
      sessionUser = {
        name: u.name ?? "—",
        email: u.email ?? "",
        avatarUrl: typeof u.image === "string" ? u.image : "",
      };
    }
  }
  // T56a — surface the maintenance banner when read-only/full is active. The Pages
  // Function is the enforcer (serves the down-page / blocks writes); this read only
  // drives the in-app notice. Cached (60 s) + fail-open to "off".
  let maintenanceMode: "off" | "read-only" | "full" = "off";
  if (env?.FEATURE_FLAGS) {
    maintenanceMode = effectiveMode(await getMaintenanceState(env), Date.now());
  }
  // T38 — the incident id is minted here, on the ROOT loader, for one reason:
  // when a child loader throws, root's data has already resolved, so the root
  // ErrorBoundary can read a server-derived id that matches the `X-Request-Id`
  // header and the server logs. Deriving it in the boundary instead would give
  // a client-only value that correlates with nothing, and would desync SSR from
  // hydration. Not personal data — a per-request correlation handle.
  const incidentId = incidentIdFromHeaders(request.headers);
  return {
    locale,
    sessionUser,
    maintenanceMode,
    incidentId,
    incidentTimestamp: formatIncidentTimestamp(new Date()),
    // T78 — the PostHog *project* key, surfaced to the browser so `AnalyticsProvider` can
    // initialise. Project keys are write-only ingest credentials, designed to be public
    // (they cannot read a single event back), which is why the same value serves both
    // halves. It is still only sent, never acted on, until analytics consent exists.
    // `null` when unset — local dev and test harnesses then run with analytics off.
    posthogKey: env?.POSTHOG_API_KEY ?? null,
    // T78 — the market bucket, derived HERE because only the server sees the request's
    // country. Client-side events could not otherwise carry it, and a segmentation that
    // covers only the server half of a funnel cannot read that funnel.
    //
    // Safe to put in the SSR document: `cacheStrategy: 'shell'` is the SERVICE WORKER
    // (per browser, network-first), not a shared edge cache, so one visitor's market is
    // never served to another. The residual is a stale value replayed from a device's own
    // offline cache after that person changed country — rare, and self-correcting on the
    // next online load.
    market: marketFromCountry(request.headers.get("CF-IPCountry")),
    // T78 — preview and production share ONE PostHog project (free plan allows one),
    // so every event must say which deployment produced it. Without it the founder's
    // own QA on a branch preview is indistinguishable from a real lead.
    analyticsEnv: analyticsEnvironment(env?.ENVIRONMENT),
    analyticsUserId,
  };
}

function showMemberBottomNav(sessionUser: NavSessionUser | null, pathname: string): boolean {
  if (!sessionUser) return false;
  if (pathname.includes("/reproductor") || pathname.includes("/player/")) return false;
  return (
    pathname.includes("/panel") ||
    pathname.includes("/dashboard") ||
    pathname.includes("/perfil") ||
    pathname.includes("/profile") ||
    pathname.includes("/soporte") ||
    pathname.includes("/support")
  );
}

/**
 * Terminal surfaces (T104: 404, offline) own the full viewport for the same reason the
 * player does — they exist to offer one clear exit, and the marketing footer under them
 * turns that into an ordinary page section. Declared via `handle.terminalSurface` rather
 * than by pathname: the 404 lives on a catch-all that matches every unresolved path, so
 * there is no pattern to test for.
 */
function isTerminalSurface(matches: ReturnType<typeof useMatches>): boolean {
  return matches.some(
    (m) => (m.handle as { terminalSurface?: boolean } | undefined)?.terminalSurface === true,
  );
}


/** `useLayoutEffect` in the browser, `useEffect` on the server (no layout to wait for). */
const useIsoLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

export default function App({ loaderData }: Route.ComponentProps) {
  const { locale, sessionUser, maintenanceMode, posthogKey, market, analyticsEnv, analyticsUserId } =
    loaderData;
  const { i18n } = useTranslation();
  const location = useLocation();
  // T102 — routes with a dark full-bleed hero flag it via `handle.darkHero`;
  // the public appbar then starts dark at top (T226 D1 deferral).
  const matches = useMatches();
  const darkHero = matches.some(
    (m) => (m.handle as { darkHero?: boolean } | undefined)?.darkHero === true,
  );
  // T872 — a funnel (the auto-test) keeps the public header with the wordmark alone.
  const logoOnlyHeader = matches.some(
    (m) => (m.handle as { logoOnlyHeader?: boolean } | undefined)?.logoOnlyHeader === true,
  );
  // Two different reasons, one consequence: the route owns the full viewport, so the
  // global header, footer and TabBar all stand down.
  const chromeStandsDown =
    // The session player owns the full viewport. `isPlayerPath` is also the rule that keeps
    // a new version from loading into the player (T890), so the two cannot drift apart.
    isPlayerPath(location.pathname) || isTerminalSurface(matches);
  // T872 S1 — which header a member gets is decided by the route's privacy, not by the
  // session alone: an unresolved path (a 404) counts as public.
  const resolvedRoute = resolveRouteFromPathname(location.pathname);
  const publicRoute = !resolvedRoute || !ROUTE_CONFIG[resolvedRoute.routeKey].isPrivate;
  // T872 (2026-10-09, the shared motion system): every in-page anchor on a public route
  // glides and lands under the header through the one hook, mounted here, once. Private
  // surfaces keep the browser's own jump (the coverage table in DESIGN.md §5 lists them).
  const mainRef = useRef<HTMLElement | null>(null);
  useSmoothAnchors(mainRef, { enabled: publicRoute });
  // T885 — empties the legacy identity caches (pre-T885 worker) on a session change.
  useSessionCacheReset(analyticsUserId);
  // The TabBar matches on pathname, so a member hitting a typo under /panel would
  // otherwise get it stacked on the 404 — including its bottom padding.
  const bottomNav = !chromeStandsDown && showMemberBottomNav(sessionUser, location.pathname);

  // The route's locale becomes i18next's before the new page paints (a layout effect, not
  // remix-i18next's `useChangeLanguage` effect): the language selector fetched its strings
  // before navigating, so the switch is synchronous and no frame shows the new route in the
  // old language — while, until it commits, the old page keeps its own (navigation brief,
  // 2026-10-10 § D).
  useIsoLayoutEffect(() => {
    if (i18n.language !== locale) void i18n.changeLanguage(locale);
  }, [locale, i18n]);

  const isCartOpen = useCartStore((s) => s.isCartOpen);
  const closeCart = useCartStore((s) => s.closeCart);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const closeAll = useCallback(() => {
    closeCart();
    setIsMobileMenuOpen(false);
  }, [closeCart]);

  useEffect(() => {
    void useCartStore.persist.rehydrate();
  }, []);

  // T775 — the app is alive. Everything a boundary reports from here on happened
  // during a navigation, not during hydration; `hydration-state.ts` explains why
  // the app's own mount is the signal rather than anything React exposes.
  useEffect(() => {
    markHydrated();
  }, []);

  // T890 — the service worker and the switch to a new version live as long as the document,
  // so they are wired here and not in `PwaProvider`, which stands down on the player.
  useUpdateSwitch();

  // T390 — the header publishes its own height, measured.
  //
  // Layout does NOT read this: the band already sizes `<main>` by measurement, which is why
  // T389 could delete `.af-shell`'s `calc(100vh - var(--app-navbar-height))`. What still
  // needs a number is the one thing CSS cannot derive — `scroll-margin-top` on an anchored
  // section, so a `#deep-link` does not land under the sticky header. Only `scroll-margin-top`
  // reads it: `.app-chrome-anchor` and the phase card's jump to `.card-hero` (app.css, T869).
  // Anything else wanting this should be sized by the band.
  //
  // So the variable stays, but it stops being a second source of truth: the header is the
  // only place the height is decided, and this publishes what it actually measured. Its old
  // value said 52px/64px while `.appbar` renders 65px/61px — wrong in both directions and
  // inverted in shape, since the constant grew across the breakpoint while the header
  // shrinks. The `:root` value in app.css is now only the pre-hydration fallback.
  useEffect(() => {
    const root = document.documentElement;
    const header = document.querySelector<HTMLElement>("header.appbar");
    if (!header) {
      // Terminal surfaces and the immersive player stand the chrome down: no header, no offset.
      root.style.setProperty("--app-navbar-height", "0px");
      return;
    }
    const publish = () =>
      root.style.setProperty(
        "--app-navbar-height",
        `${Math.round(header.getBoundingClientRect().height)}px`,
      );
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(header);
    return () => observer.disconnect();
  }, [chromeStandsDown, sessionUser, publicRoute]);

  return (
    <html lang={locale} dir={i18n.dir()} suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#0A0908" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        <meta name="apple-mobile-web-app-title" content="TheFittClub" />
        {/* T52 — per-locale manifest. Both files share one `id`, so this is one install
            whose start_url follows the language she installed from, not two apps. */}
        <link rel="manifest" href={`/manifest.${locale}.webmanifest`} />
        <Meta />
        <Links />
      </head>
      {/* No `min-h-screen` here: the band below owns the viewport claim, and one owner is
          the whole point. Stacking them also meant the outer claim was `100vh` over an
          inner `100dvh` — two units that disagree on mobile, where the browser chrome
          moves. The body grows to at least the band's height on its own. */}
      <body className="flex flex-col bg-primary text-textMain font-sans selection:bg-darkBg selection:text-white">
        <SkipToContent />

        {/* The viewport band: top chrome + <main>, exactly one screen tall.
            `<main>` is the only flex-grow child, so it takes 100dvh MINUS whatever the
            header (and the maintenance banner, when shown) actually measured — no
            constant to drift. This is the auto-test stage's resolution
            (`.at-stage.has-chrome`) raised to the shell, and it is what gives a short
            page its full screen and puts the footer exactly at the fold: you start
            scrolling and the footer is the next thing you meet.
            The footer sits OUTSIDE the band on purpose — inside it, a 720px footer would
            eat the screen and leave <main> with the scraps. TabBar is `position: fixed`
            (app.css:2944), so it does not belong to the band either. */}
        <div className="flex min-h-dvh flex-col">
          {/* T98 rebrand: guests get AppHeader + cúter; members get MemberHeader
              (its avatar menu carries profile + sign-out). */}
          {/* T872 S1 (R-T872-3): a member keeps the public header on public routes —
              same four links, «Mi espacio» where a guest sees «Iniciar sesión»; the
              member header and its tab bar stay on private routes. */}
          {chromeStandsDown ? null : sessionUser && !publicRoute ? (
            <MemberHeader sessionUser={sessionUser} darkAtTop={darkHero} />
          ) : (
            <>
              <AppHeader
                onMenuOpen={() => setIsMobileMenuOpen(true)}
                menuOpen={isMobileMenuOpen}
                darkAtTop={darkHero}
                sessionUser={sessionUser}
                logoOnly={logoOnlyHeader}
              />
              {/* The public bar is fixed; this keeps its rest height in the layout, so
                  compacting or hiding it never moves the page (R-T872-2). */}
              <div className="appbar-space" aria-hidden="true" />
            </>
          )}

          {maintenanceMode !== "off" ? (
            <MaintenanceBanner mode={maintenanceMode} statusHref={`/${locale}/status`} />
          ) : null}

          <main
            ref={mainRef}
            id="main-content"
            tabIndex={-1}
            className={cn(
              "flex w-full min-w-0 min-h-0 flex-1 flex-col",
              bottomNav &&
                "pb-[max(5.5rem,calc(4.5rem+env(safe-area-inset-bottom,0px)))] md:pb-0",
            )}
          >
            {/* T78 — wraps `<Outlet />` rather than the whole body: every surface that
                captures an event lives under it, and scoping it here keeps the chrome
                (header, footer, cookie banner) out of a context none of them read. */}
            <AnalyticsProvider
              posthogKey={posthogKey}
              market={market}
              environment={analyticsEnv}
              userId={analyticsUserId}
              pathname={location.pathname}
            >
              <Suspense fallback={<div aria-busy="true" />}>
                <RouteTranslations><Outlet /></RouteTranslations>
              </Suspense>
            </AnalyticsProvider>
          </main>
        </div>

        {bottomNav ? <TabBar /> : null}

        {/* T393 — the footer stands down under the tab bar. NOT folded into
            `chromeStandsDown`: that switch also stands the header and the TabBar down, and
            here both must stay. The class carries the width half (`max-width: 767.98px`),
            which is the only width where `.tabbar` renders at all. */}
        {chromeStandsDown ? null : (
          <Footer sessionUser={sessionUser} className={bottomNav ? "is-app-chrome" : undefined} />
        )}

        <UiOverlay
          isOpen={isCartOpen || isMobileMenuOpen}
          onClick={closeAll}
        />
        <CartSidebar />
        {/* The cúter belongs to the public header: guests everywhere, members on public
            routes (T872 S1); on private routes the member header's avatar sheet serves. */}
        {sessionUser && !publicRoute ? null : (
          <Cuter isOpen={isMobileMenuOpen} onClose={closeAll} sessionUser={sessionUser} />
        )}
        <CookieBanner />

        {/* T52 — the offline-queue drain and its toast. The player stands the rest of the
            chrome down and owns the viewport, so the toast stands down with it. The
            service worker is registered above, for every document (T890), and the update
            banner that used to live here is gone. T393 took the provider's props with the
            floating install card. */}
        {chromeStandsDown ? null : <PwaProvider />}

        {/* T134 — site-wide Organization + WebSite structured data. */}
        <JsonLd data={[organizationJsonLd(), websiteJsonLd(locale === "en" ? "en" : "es")]} />

        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

/** Root loader data as the ErrorBoundary sees it — absent when root's own loader threw. */
type RootIncidentData = { incidentId?: string; incidentTimestamp?: string };

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  const { i18n } = useTranslation();
  // T775 — the same four triage tags the route boundary now carries. Root IS
  // decorated by the build-time transform (it declares its boundary as a function,
  // the one module of 70 that does), so `error` here is real. The tags are for the
  // day it is not: `route_id` says which screen, `error_type` what arrived,
  // `from_path` where she came from, `hydrating` whether the app had started.
  const errorRouteId = useLeafRouteId();
  // T104 — the boundary renders its own document, outside the app chrome, so
  // the surface owns the <main> landmark (standalone). A 404 thrown by a loader
  // gets the not-found surface; everything else gets the 500 one.
  //
  // T38 — the 4xx/5xx split. `status` is the HTTP status the surface stands for:
  // a thrown Response carries its own; a bare Error is an unhandled server fault
  // and is a 500 by definition. Only 5xx gets the incident treatment — a 404 or a
  // 403 is a normal answer, not an incident, and stamping an id on it would flood
  // the channel with noise and invite members to report their own typos.
  const status = isRouteErrorResponse(error) ? error.status : 500;
  const isNotFound = status === 404;
  const isServerFault = status >= 500;

  // The id comes from root's loader (server-derived, matches `X-Request-Id`)
  // whenever root resolved — the common case, since a child loader throwing
  // means root already succeeded. When root's OWN loader threw there is no
  // loader data, and the spec's fallback applies: mint one client-side.
  //
  // The fallback is deliberately deferred to an effect. Minting during render
  // would produce different values on the server and on the client and tear
  // hydration apart; rendering nothing first and filling in on mount is the
  // reason ErrorPage500 treats the incident rows as optional.
  const rootData = useRouteLoaderData("root") as RootIncidentData | undefined;
  const [fallback, setFallback] = useState<RootIncidentData | null>(null);
  const needsFallback = isServerFault && !rootData?.incidentId;

  useEffect(() => {
    if (!needsFallback) return;
    setFallback({
      incidentId: randomIncidentId(),
      incidentTimestamp: formatIncidentTimestamp(new Date()),
    });
  }, [needsFallback]);

  const incidentId = isServerFault ? (rootData?.incidentId ?? fallback?.incidentId) : undefined;
  const incidentTimestamp = isServerFault
    ? (rootData?.incidentTimestamp ?? fallback?.incidentTimestamp)
    : undefined;

  // T38 — every render of the 500 surface emits one event on the T27 channel
  // (Sentry since the T123 transport split), carrying exactly the fields the
  // spec names and nothing else. Fingerprinted by status + pathname so a broken
  // route groups into one issue with the incident ids as tags, instead of one
  // issue per visitor.
  //
  // Distinct from the exception capture below: that reports *what threw*, this
  // reports *that a member was shown the failure page* — the two diverge
  // whenever the throw happened server-side (already captured there) but the
  // surface renders here.
  useEffect(() => {
    if (!isServerFault || !incidentId) return;
    try {
      Sentry.withScope((scope) => {
        scope.setLevel("error");
        scope.setFingerprint(["error-page-5xx", String(status), window.location.pathname]);
        scope.setTag("incident_id", incidentId);
        scope.setTag("http_status", String(status));
        scope.setContext("incident", {
          incident_id: incidentId,
          pathname: window.location.pathname,
          referrer: safeReferrer(document.referrer),
          http_status: status,
          timestamp_utc: incidentTimestamp ?? "",
          user_agent: navigator.userAgent.slice(0, 256),
        });
        Sentry.captureMessage(`error_page_5xx_rendered:${status}`);
      });
    } catch {
      // Observability must never break the fallback UI.
    }
  }, [isServerFault, incidentId, incidentTimestamp, status]);

  // T806 — which classes this effect reports is declared in
  // `~/lib/error-ownership`, not here. The comment that stood in this place
  // asserted the server's behaviour across a module boundary, the server's
  // comment asserted this one's, and the class between them (an unexpected 400)
  // was reported by neither. PII scrub mirrors RouteErrorBoundary's pattern —
  // never pass raw `error` to captureException (could carry user-input form
  // values in its message — T123 review fix).
  //
  // The `error_page_5xx_rendered` effect above is NOT a second reporter of the
  // same class: it says "a member was shown the failure page", is fingerprinted
  // separately, and is deliberately outside the ownership table. Said out loud so
  // nobody counts it as a double and removes it.
  //
  // T38 — this waits for `incidentId` before firing, so the capture is never
  // sent untagged. A non-route error is always treated as a 500, so an id always
  // arrives: immediately from root's loader data, or one tick later from the
  // client-side fallback when root's own loader was what threw. `incidentId` is
  // therefore in the deps, and it only ever transitions undefined → value once.
  //
  // `capturedRef` is NOT what makes that single — the early return already does,
  // and removing the ref leaves the tests green (verified by mutation). What it
  // actually covers is StrictMode's deliberate double-invocation of effects in
  // development (`entry.client.tsx` wraps the tree), which would otherwise post
  // the same exception twice into Sentry's `development` bucket. Pre-existing
  // behaviour of this effect, cheap to fix while here; inert in production.
  const capturedRef = useRef(false);
  useEffect(() => {
    if (!boundaryReports(error)) return;
    if (!error) return;
    if (!incidentId) return;
    if (capturedRef.current) return;
    capturedRef.current = true;
    try {
      const message = error instanceof Error ? error.message : String(error);
      const stack = error instanceof Error ? (error.stack ?? "") : "";
      const synthetic = new Error(scrubPii(message).slice(0, 500));
      synthetic.name = error instanceof Error ? error.constructor.name : "UnknownError";
      synthetic.stack = scrubPii(stack).slice(0, 2000);
      Sentry.withScope((scope) => {
        scope.setTag("source", "client");
        scope.setTag("boundary", "root");
        // T38 — same join key as the server-side capture and the
        // `X-Request-Id` header, so the exception and the "she saw the failure
        // page" event resolve to one incident in Sentry rather than two
        // unrelated ones.
        scope.setTag("incident_id", incidentId);
        scope.setTag("route_id", errorRouteId ?? "unknown");
        scope.setTag("error_type", error === null ? "null" : typeof error);
        scope.setTag("from_path", safeReferrer(document.referrer) || "none");
        scope.setTag("hydrating", String(isHydrating()));
        Sentry.captureException(synthetic);
      });
    } catch {
      // Fire-and-forget; observability must not break the fallback UI.
    }
  }, [error, incidentId, errorRouteId]);

  return (
    <html lang={i18n.language} dir={i18n.dir()} suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="robots" content="noindex, nofollow" />
        <Meta />
        <Links />
      </head>
      {/* Same rule as the App shell: `.es-page.is-standalone` owns the viewport claim
          (100dvh). A `min-h-screen` here would stack a second, `vh`-based one on top. */}
      <body className="flex flex-col bg-primary text-textMain font-sans">
        {isNotFound ? (
          <ErrorPage404 standalone />
        ) : (
          <ErrorPage500
            standalone
            incidentId={incidentId}
            timestampUtc={incidentTimestamp}
          />
        )}
        <Scripts />
      </body>
    </html>
  );
}
